/**
 * Durations, and the thresholds more than one process must agree about.
 *
 * The arithmetic is not the risk; nobody gets an hour wrong. The thresholds
 * built on it are: `/scans` and `ops-report.service.ts` both call a run stuck
 * after an hour, and used to hold that hour separately.
 */

export const SECOND_MS = 1000;
export const MINUTE_MS = 60 * SECOND_MS;
export const HOUR_MS = 60 * MINUTE_MS;
export const DAY_MS = 24 * HOUR_MS;
export const WEEK_MS = 7 * DAY_MS;

/**
 * Claimed but unfinished for this long is dead, not slow. One number for a
 * stuck scan, a photo with no fingerprint, and the ops report counting both —
 * a page drawing the line elsewhere calls the queue healthy while the report
 * pages someone.
 */
export const STUCK_AFTER_MS = HOUR_MS;

/** `running` past `STUCK_AFTER_MS`. Not a stored status: the process died
 *  without saying so, and the row will say `running` for ever. */
export function isScanStuck(run: { status: string; startedAt: string }, now = Date.now()): boolean {
  return run.status === 'running' && now - new Date(run.startedAt).getTime() > STUCK_AFTER_MS;
}

/** The last `count` calendar months, newest first, as `YYYY-MM` in UTC — the
 *  boundaries `/account/reports/monthly` counts by. */
export function recentMonths(count: number, now = new Date()): string[] {
  const at = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const out: string[] = [];
  for (let i = 0; i < count; i++) {
    out.push(at.toISOString().slice(0, 7));
    at.setUTCMonth(at.getUTCMonth() - 1);
  }
  return out;
}

/** "October 2026" for `2026-10`. */
export function monthLabel(month: string): string {
  return new Date(`${month}-01T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * How often a screen with no push channel asks again. Eleven screens had their
 * own figure and no way to tell a decision from a default. The decisions:
 *
 * - `LIVE` — worked in while open; a stale row means duplicating a colleague.
 * - `PANEL` — a monitoring list; twenty open tabs must not be a load profile.
 * - `SLOW` — figures that move on a billing period.
 * - `SETTLING` — not monitoring: waiting once on a webhook, then stopping.
 */
export const POLL_MS = {
  LIVE: 15 * SECOND_MS,
  PANEL: 20 * SECOND_MS,
  SLOW: 30 * SECOND_MS,
  SETTLING: 1_200,
} as const;

/**
 * How often an open SSE stream writes a heartbeat so nothing in the middle
 * (nginx, Cloudflare's idle window, a corporate proxy) closes a quiet
 * connection. One figure for every stream — deploys, panel, live search — so
 * they cannot drift apart; well under any common proxy idle window.
 */
export const SSE_HEARTBEAT_MS = 15 * SECOND_MS;

/** About ten seconds all told, well past the webhook's usual lag. After that
 *  the page says what it knows rather than spinning. */
export const SETTLE_ATTEMPTS = 8;
